import { z } from "zod";
import { PRODUCT_TYPES } from "../catalog-products.js";
import { defineEndpoint } from "../endpoint.js";
import { stockQtySchema } from "../inventory.js";
import { paisa } from "../money.js";
import { PAYMENT_ACCOUNT_TYPES } from "../payments.js";
/**
 * POS sync, pull side (docs/specs/pos-sync.md, B-42): device token, bootstrap and the change feed. The device
 * applies every row as an idempotent upsert (or delete) keyed by `entity` + `id`.
 */
export const SYNC_ENTITIES = [
    "item",
    "category",
    "stock",
    "customer",
    "staff",
    "settings",
    "location",
    "register",
    "payment_account",
];
export const syncEntitySchema = z.enum(SYNC_ENTITIES);
/** A sellable variant as the POS sees it at its location (price = the location's override, else the variant's). */
export const syncItemSchema = z.object({
    id: z.uuid(),
    productId: z.uuid(),
    name: z.string(),
    /** Option values joined with " / " (`Red / XL`); empty for a product without options. */
    variantLabel: z.string(),
    sku: z.string(),
    barcodes: z.array(z.string()),
    productType: z.enum(PRODUCT_TYPES),
    categoryId: z.uuid().nullable(),
    unitCode: z.string(),
    unitPrecision: z.int(),
    pricePaisa: paisa(),
    mrpPaisa: paisa().nullable(),
    /** The product's rate, else its category's default, else 0. */
    taxRateBp: z.int(),
    trackInventory: z.boolean(),
    allowNegative: z.boolean(),
    /** `active` only when both the variant and its product are active. */
    status: z.enum(["active", "archived"]),
});
export const syncCategorySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    parentId: z.uuid().nullable(),
    path: z.string(),
    depth: z.int(),
});
/** Stock of one variant at the device's location (`id` = variant). */
export const syncStockSchema = z.object({ id: z.uuid(), onHand: stockQtySchema, reserved: stockQtySchema });
export const syncCustomerSchema = z.object({
    id: z.uuid(),
    phone: z.string(),
    name: z.string(),
    creditLimitPaisa: paisa(),
    balancePaisa: paisa(),
});
/** A user who may work at the device's location: their permission keys there and POS PIN hash (null: not set). */
export const syncStaffSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    permissions: z.array(z.string()),
    pinHash: z.string().nullable(),
});
/** The business settings a POS prints and calculates with (`id` = `business`). */
export const syncSettingsSchema = z.object({
    id: z.literal("business"),
    name: z.string(),
    address: z.string(),
    phone: z.string().nullable(),
    bin: z.string().nullable(),
    invoiceFooter: z.string(),
    taxMode: z.enum(["inclusive", "exclusive"]),
    timezone: z.string(),
    customerLocale: z.string(),
    discountLimitBp: z.int(),
    refundLimitPaisa: paisa(),
});
export const syncLocationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    billPrefix: z.string().nullable(),
    address: z.string(),
    phone: z.string().nullable(),
    status: z.enum(["active", "inactive"]),
});
export const syncRegisterSchema = z.object({
    id: z.uuid(),
    locationId: z.uuid(),
    name: z.string(),
    status: z.enum(["active", "inactive"]),
});
export const syncPaymentAccountSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    type: z.enum(PAYMENT_ACCOUNT_TYPES),
    registerId: z.uuid().nullable(),
    status: z.enum(["active", "archived"]),
});
const rowOf = (entity, data) => z.object({ entity: z.literal(entity), id: z.string(), data });
/** One entity's current state, as bootstrap rows and pulled upserts carry it. */
export const syncRowSchema = z.discriminatedUnion("entity", [
    rowOf("item", syncItemSchema),
    rowOf("category", syncCategorySchema),
    rowOf("stock", syncStockSchema),
    rowOf("customer", syncCustomerSchema),
    rowOf("staff", syncStaffSchema),
    rowOf("settings", syncSettingsSchema),
    rowOf("location", syncLocationSchema),
    rowOf("register", syncRegisterSchema),
    rowOf("payment_account", syncPaymentAccountSchema),
]);
/** A pulled change: `upsert` with the entity's current state, or `delete` (gone, or no longer for this device). */
export const syncChangeSchema = z.union([
    syncRowSchema.and(z.object({ seq: z.int().min(1), op: z.literal("upsert") })),
    z.object({ seq: z.int().min(1), op: z.literal("delete"), entity: syncEntitySchema, id: z.string() }),
]);
// Device token -----------------------------------------------------------------------------------------------------
export const deviceTokenSchema = z.object({ accessToken: z.string(), expiresAt: z.iso.datetime() });
// State, bootstrap, pull ------------------------------------------------------------------------------------------
export const syncStateSchema = z.object({
    deviceId: z.uuid(),
    tenantId: z.uuid(),
    locationId: z.uuid(),
    registerId: z.uuid(),
    serverTime: z.iso.datetime(),
    /** The tenant's newest feed seq, and the oldest cursor still served (older → `RESYNC_REQUIRED`). */
    feed: z.object({ lastSeq: z.int().min(0), floorSeq: z.int().min(0) }),
    /** The last `deviceSeq` the server processed for this device (B-51): the first call after a restore. */
    serverLastSeq: z.int().min(0),
});
export const BOOTSTRAP_DATASETS = [
    "items",
    "categories",
    "stock",
    "customers",
    "staff",
    "settings",
    "payment_accounts",
];
export const bootstrapDatasetSchema = z.enum(BOOTSTRAP_DATASETS);
export const bootstrapManifestSchema = z.object({
    /** Read before any data: pull from here after the last page, so nothing changed meanwhile is missed. */
    snapshotSeq: z.int().min(0),
    datasets: z.array(z.object({ name: bootstrapDatasetSchema, count: z.int().min(0) })),
});
export const BOOTSTRAP_PAGE_MAX = 2000;
export const bootstrapPageQuerySchema = z.object({
    /** The `nextAfter` of the previous page; absent for the first. */
    after: z.string().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(BOOTSTRAP_PAGE_MAX).default(BOOTSTRAP_PAGE_MAX),
});
export const bootstrapPageParamsSchema = z.object({ dataset: bootstrapDatasetSchema });
export const bootstrapPageSchema = z.object({
    rows: z.array(syncRowSchema),
    nextAfter: z.string().nullable(),
});
export const PULL_PAGE_MAX = 1000;
export const pullQuerySchema = z.object({
    cursor: z.coerce.number().int().min(0),
    limit: z.coerce.number().int().min(1).max(PULL_PAGE_MAX).default(500),
});
export const pullPageSchema = z.object({
    changes: z.array(syncChangeSchema),
    /** Store this as the new cursor (in the same local transaction as the changes). */
    nextCursor: z.int().min(0),
    hasMore: z.boolean(),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "pos-sync" };
const device = { device: true };
export const issueDeviceToken = defineEndpoint({
    ...tag,
    name: "issueDeviceToken",
    method: "POST",
    path: "/v1/pos/token",
    access: { public: true },
    summary: "A 15-minute device token for `Authorization: Device <credential>`; a revoked device gets DEVICE_REVOKED",
    status: 200,
    response: deviceTokenSchema,
    errors: ["DEVICE_REVOKED"],
});
export const getSyncState = defineEndpoint({
    ...tag,
    name: "getSyncState",
    method: "GET",
    path: "/v1/pos/sync/state",
    access: device,
    summary: "The device's binding, server time and the feed's range",
    status: 200,
    response: syncStateSchema,
});
export const startBootstrap = defineEndpoint({
    ...tag,
    name: "startBootstrap",
    method: "POST",
    path: "/v1/pos/sync/bootstrap",
    access: device,
    summary: "Starts a full download: the feed seq to pull from afterwards and the size of each dataset",
    status: 200,
    response: bootstrapManifestSchema,
});
export const getBootstrapPage = defineEndpoint({
    ...tag,
    name: "getBootstrapPage",
    method: "GET",
    path: "/v1/pos/sync/bootstrap/{dataset}",
    access: device,
    summary: "One page of a dataset, keyset by id",
    params: bootstrapPageParamsSchema,
    query: bootstrapPageQuerySchema,
    status: 200,
    response: bootstrapPageSchema,
});
export const pullChanges = defineEndpoint({
    ...tag,
    name: "pullChanges",
    method: "GET",
    path: "/v1/pos/sync/changes",
    access: device,
    summary: "Changes after a cursor for this device's location; a stale cursor answers RESYNC_REQUIRED",
    query: pullQuerySchema,
    status: 200,
    response: pullPageSchema,
    errors: ["RESYNC_REQUIRED"],
});
//# sourceMappingURL=feed.js.map